import { Tabs } from '@mantine/core';
import { ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Badge } from '@mantine/core';

type TabsConfig<T extends string> = {
  title: string;
  key: T;
  isLoading?: boolean;
  count?: number;
};

export function RoutesTabbarFactory<TabKey extends string>(
  tabs: Array<TabKey>,
  paramsName: string,
) {
  const defaultTab = tabs[0];
  const validateTab = z.enum(tabs).optional().parse;

  // 可能要送 basepath？如果 navigateTab 要實作的話
  const useTabBar = () => {
    const navigate = useNavigate();
    const params = useParams();

    const currentTab = validateTab(params?.[paramsName]);

    // caution, just the replacement of latest path
    const navigateTab = (t: TabKey) => {
      navigate(`./../${t}`, {
        relative: 'path',
      });
    };

    return {
      currentTab,
      navigateTab,
    };
  };

  function Tabbar({
    current,
    tabsConfig,
    onChange,
    children,
    shouldRedirectToDefault = false,
  }: {
    current: TabKey | undefined;
    tabsConfig: Array<TabsConfig<TabKey>>;
    onChange: (v: TabKey | undefined) => void;
    children: ReactNode;
    shouldRedirectToDefault: boolean;
  }) {
    if (!current && shouldRedirectToDefault) {
      return <Navigate to={`./${defaultTab}`} />;
    }

    return (
      <Tabs
        defaultValue={defaultTab}
        value={current}
        onChange={(value) => {
          if (value === null || !tabs.includes(value as TabKey)) {
            onChange(undefined);
            return;
          }
          onChange(value as TabKey);
        }}
      >
        <Tabs.List>
          {tabsConfig.map(({ key, title, count }) => {
            return (
              <Tabs.Tab
                key={key}
                value={key}
                rightSection={
                  !!count && (
                    <Badge size="xs" variant="filled">
                      {count}
                    </Badge>
                  )
                }
              >
                {title}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
        {children}
      </Tabs>
    );
  }

  type TabsPanelProps = React.ComponentProps<typeof Tabs.Panel>;

  function TabPanel({
    tabKey,
    ...props
  }: { tabKey: TabKey } & Omit<TabsPanelProps, 'value'>) {
    return <Tabs.Panel value={tabKey} {...props} />;
  }

  return [useTabBar, Tabbar, TabPanel] as const;
}
